import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";

import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";

import { PATIENT_HEADER_QUERY } from "features/patients/use-patient.hook";
import { useGetAppointmentQuery } from "services/appointments";
import { useGetPatientQuery } from "services/patients";
import { Appointment } from "services/types/appointments.type";
import type { Patient } from "services/types/patients.type";

import { DRAWER_MOBILE_WIDTH, DRAWER_WIDTH } from "./patient-drawer.component";

const inboxRegex = /^\/inbox\/(\d+)$/;
export function usePatientDrawerProvider() {
  const [isOpen, setIsOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [disableClose, setDisableClose] = useState(false);
  const [patientId, setPatientId] = useState<number>(-1);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("mobile"));
  const { data: patient, isFetching } = useGetPatientQuery(
    { patientId, query: PATIENT_HEADER_QUERY },
    {
      skip: patientId === -1
    }
  );

  const { data: upcomingAppointment = null } = useGetAppointmentQuery(
    { id: patient?.next_appointment?.id ?? -1 },
    {
      skip: !patient?.next_appointment?.id
    }
  );

  const { data: pastAppointment = null } = useGetAppointmentQuery(
    { id: patient?.previous_appointment?.id ?? -1 },
    {
      skip: !patient?.previous_appointment?.id
    }
  );

  const isInboxPage = inboxRegex.test(location.pathname);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setDisableClose(false);
  }, []);

  useEffect(() => {
    return () => {
      handleClose();
    };
  }, [handleClose, location.pathname]);

  const rightOffset = useMemo(() => {
    if (!isOpen) return 0;
    if (isInboxPage && isMobile) {
      return DRAWER_MOBILE_WIDTH;
    }

    return DRAWER_WIDTH;
  }, [isInboxPage, isMobile, isOpen]);

  const handleOpen = useCallback((patientId?: number) => {
    if (typeof patientId === "number" || patientId !== -1) {
      setPatientId(Number(patientId));
    }
    setIsOpen(true);
  }, []);

  const handleResize = useCallback(() => {
    if (!isMobile) {
      setIsCollapsed(false);
    }
  }, [isMobile]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  return {
    isOpen,
    patient,
    isFetching,
    upcomingAppointment: patient?.next_appointment?.id
      ? upcomingAppointment
      : null,
    pastAppointment: patient?.previous_appointment?.id ? pastAppointment : null,
    handleOpen,
    handleClose,
    disableClose,
    setDisableClose,
    isCollapsed,
    setIsCollapsed,
    rightOffset,
    isCollapsible: isInboxPage
  };
}

const defaultPatientDrawerContext = {
  isOpen: false,
  isFetching: false,
  patient: undefined,
  upcomingAppointment: null,
  pastAppointment: null,
  handleOpen: () => {},
  handleClose: () => {},
  disableClose: false,
  setDisableClose: () => {},
  isCollapsed: true,
  setIsCollapsed: () => {},
  rightOffset: 0,
  isCollapsible: false
};

export const PatientDrawerContext = createContext<{
  isOpen: boolean;
  isFetching: boolean;
  patient?: Patient;
  upcomingAppointment: Appointment | null;
  pastAppointment: Appointment | null;
  handleOpen: (patientId?: number) => void;
  handleClose: () => void;
  disableClose: boolean;
  setDisableClose: React.Dispatch<React.SetStateAction<boolean>>;
  isCollapsed: boolean;
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  rightOffset: number;
  isCollapsible: boolean;
}>(defaultPatientDrawerContext);

type PatientDrawerProviderProps = {
  children?: ReactNode;
};

export const PatientDrawerProvider = ({
  children
}: PatientDrawerProviderProps) => {
  const {
    isOpen,
    patient,
    isFetching,
    upcomingAppointment,
    pastAppointment,
    handleOpen,
    handleClose,
    disableClose,
    setDisableClose,
    isCollapsed,
    setIsCollapsed,
    rightOffset,
    isCollapsible
  } = usePatientDrawerProvider();

  const value = useMemo(
    () => ({
      isOpen,
      patient,
      isFetching,
      upcomingAppointment,
      pastAppointment,
      handleOpen,
      handleClose,
      disableClose,
      setDisableClose,
      isCollapsed,
      setIsCollapsed,
      rightOffset,
      isCollapsible
    }),
    [
      isOpen,
      patient,
      isFetching,
      upcomingAppointment,
      pastAppointment,
      handleOpen,
      handleClose,
      disableClose,
      setDisableClose,
      isCollapsed,
      setIsCollapsed,
      rightOffset,
      isCollapsible
    ]
  );

  return (
    <PatientDrawerContext.Provider value={value}>
      {children}
    </PatientDrawerContext.Provider>
  );
};

export const usePatientDrawer = () => {
  const patientDrawerContext = useContext(PatientDrawerContext);

  if (patientDrawerContext === defaultPatientDrawerContext) {
    const isDev = process.env.NODE_ENV === "development";

    if (isDev) {
      console.warn(
        "usePatientDrawer: Using default context values. This may happen during hot reloads or if the component is not wrapped with PatientDrawerProvider."
      );
    } else {
      console.error(
        "usePatientDrawer must be used within a PatientDrawerProvider"
      );
    }
  }

  return patientDrawerContext;
};
